import {h, Fragment} from "preact"
import { useFacets, useFacet } from "@nosto/preact"
import { useEffect, useState, useRef } from "preact/hooks"
import { render } from "preact"

const FACET_ORDER = {
    'Size': 0,
    'Body Material': 1,
    'Body Color': 2,
    'Rim Color': 3,
    'BU Size': 4,
    'Liner Type': 5,
    'Availability': 6,
    'Price': 7
};

function sortFacets(facets) {
    return [...facets].sort((a, b) => {
        const orderA = FACET_ORDER[a.name] ?? Infinity;
        const orderB = FACET_ORDER[b.name] ?? Infinity;
        return orderA - orderB;
    });
}

function MobileFacet({ facet, pendingChanges, onPendingChange }) {
    return (
        <details id={`Details-Mobile-${facet.id}`} class="facets__disclosure-vertical js-filter mobile-facets-details" data-index={facet.id} open>
            <summary class="mobile facets__summary caption-large focus-offset" aria-label={`${facet.name} (0 selected)`} role="button" aria-expanded="true" aria-controls={`Facet-${facet.id}`}>
                <div class="filters-summary">
                    <span class="facets__summary-label P-14">{facet.name}</span>
                    <svg aria-hidden="true" focusable="false" class="icon icon-caret" xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
                        <path d="M12.2402 7.25L7.99753 11.4926L3.75489 7.25" stroke="#333333" />
                    </svg>
                </div>
            </summary>
            <div id={`Facet-${facet.id}`} class="parent-display facets__display-vertical">
                <fieldset class="facets-wrap parent-wrap facets-wrap-vertical mobile-facets-fieldset">
                    <legend class="visually-hidden">{facet.name}</legend>
                    <ul class="facets-layout facets-layout-list facets-layout-list--text facets__list--vertical list-unstyled mobile-facets-ul" role="list">
                        {facet.data?.map((item, optIndex) => {
                            let displayValue;
                            if (facet.field === 'extra.custom-variant_body_material' && item.value === 'Vinyl') {
                                displayValue = 'Steeletex';
                            } else if (item.value === 'InStock') {
                                displayValue = 'In Stock';
                            } else {
                                displayValue = item.value;
                            }

                            // Check if there's a pending change for this item
                            const pendingChange = pendingChanges[facet.field]?.[item.value]
                            const isSelected = pendingChange !== undefined ? pendingChange : item.selected

                            return (
                                <li class="list-menu__item facets__item mobile-facets-li">
                                    <label for={`Filter-${facet.field}-${optIndex + 1}`} class={`facets__label facet-checkbox${item.count === 0 ? ' disabled' : ''} ${isSelected ? 'active' : ''}`}>
                                        <input 
                                            type="checkbox"
                                            name={facet.field}
                                            value={item.value}
                                            id={`Filter-${facet.field}-${optIndex + 1}`}
                                            disabled={item.count === 0}
                                            checked={isSelected}
                                            onChange={() => onPendingChange(facet.field, item.value, !isSelected)}
                                        />
                                        <span class="input-text-value" aria-hidden="true">
                                            <div class="value-wrapper">
                                                {facet.field.includes('color') && <div class={`value-color-dot ${item.value.toLowerCase()}`} />}
                                                <span class="Button-C">{displayValue}</span>
                                            </div>
                                        </span>
                                        <span class="visually-hidden">{displayValue} ({item.count} products)</span>
                                    </label>
                                </li>
                            )
                        })}
                    </ul>
                </fieldset>
            </div>
        </details>
    )
}

function MobilePriceFacet() {
    const ranges = [
        { min: "10", max: "50" },
        { min: "50", max: "100" },
        { min: "100", max: "200" },
        { min: "200", max: "300" },
        { min: "300", max: "500" },
        { min: "500+", max: "" }
    ];

    return (
        <details id="Details-Mobile-filter.v.price" class="facets__disclosure-vertical js-filter mobile-fatets-details" data-index="price" open>
            <summary class="facets__summary caption-large focus-offset mobile facets__summary" role="button" aria-expanded="true">
                <div class="filters-summary price">
                    <span class="Button-C">Price</span>
                    <svg aria-hidden="true" focusable="false" class="icon icon-caret" xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
                        <path d="M12.2402 7.25L7.99753 11.4926L3.75489 7.25" stroke="#333333" />
                    </svg>
                </div>
            </summary>
            <div class="facets__display-vertical">
                <div class="facets__header-vertical"></div>
                <div class="facets__price">
                    <div class="ranges">
                        {ranges.map((range, idx) => (
                            <div class="mt12" key={idx}>
                                <label class="sc-checkbox">
                                    <input 
                                        name={`range[${idx}]`} 
                                        type="checkbox" 
                                        data-min={range.min} 
                                        data-max={range.max}
                                    />
                                    <span class="sc-icon"></span>
                                    <span class="sc-label">
                                        <p class="Button-C">
                                            ${range.min}{range.max ? ` to $${range.max}` : ''}
                                        </p>
                                    </span>
                                </label>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </details>
    )
}

export default function MobileFacetsModal({ isOpen, onClose }) {
    const { facets } = useFacets()
    const initialFacets = sortFacets(facets)
    const [pendingChanges, setPendingChanges] = useState({})
    const facetControls = useRef(initialFacets.map(facet => useFacet(facet.id)))

    const handlePendingChange = (field, value, selected) => {
        setPendingChanges(prev => ({
            ...prev,
            [field]: {
                ...(prev[field] || {}),
                [value]: selected
            }
        }))
    }

    const handleApply = (e) => {
        e.preventDefault()
        // Apply all pending changes using stored facet controls
        Object.entries(pendingChanges).forEach(([field, values]) => {
            const facet = initialFacets.find(f => f.field === field)
            const facetControl = facetControls.current[initialFacets.indexOf(facet)]
            if (facetControl) {
                Object.entries(values).forEach(([value, selected]) => {
                    facetControl.toggleProductFilter(field, value, selected)
                })
            }
        })
        setPendingChanges({})
        onClose()
    }

    const handleClearAll = (e) => {
        e.preventDefault()
        // Clear all selected filters using stored facet controls
        initialFacets.forEach((facet, index) => {
            const { toggleProductFilter } = facetControls.current[index]
            facet.data.forEach(item => {
                if (item.selected) {
                    toggleProductFilter(facet.field, item.value, false)
                }
            })
        })
        setPendingChanges({})
        onClose()
    }

    const modalContent = (
        <>
            <div class="mobile-facets__header" style={{ padding: '20px 16px', borderBottom: '1px solid var(--color-line-gray)' }}>
                <div class="mobile-facets__header-inner">
                    <h2 class="mobile-facets__heading medium-hide large-up-hide Eyebrow-A">Filter</h2>
                    <h2 class="mobile-facets__heading small-hide Eyebrow-A">Filter</h2>
                </div>
                <div class="mobile-facets__close" onClick={onClose} style={{ position: 'absolute', top: '20px', right: '16px' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-close" fill="none" viewBox="0 0 18 17">
                        <path d="M.865 15.978a.5.5 0 00.707.707l7.433-7.431 7.579 7.282a.501.501 0 00.846-.37.5.5 0 00-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 10-.707-.708L8.991 7.853 1.413.573a.5.5 0 10-.693.72l7.563 7.268-7.418 7.417z" fill="currentColor" />
                    </svg>
                </div>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px' }}>
                {initialFacets.map(facet => (
                    <MobileFacet 
                        key={facet.id} 
                        facet={facet} 
                        pendingChanges={pendingChanges}
                        onPendingChange={handlePendingChange}
                    />
                ))}
                <MobilePriceFacet />
            </div>

            <div class="mobile-facets__footer" style={{
                padding: '16px',
                borderTop: '1px solid var(--color-line-gray)',
                background: 'white',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <button type="button" class="apply-button" onClick={handleApply}>
                    <span class="Button-A">Apply</span>
                </button>
                <div 
                    class="filters-clear mobile-facets-footer-clear Button-A" 
                    onClick={handleClearAll}
                    role="button"
                    style={{ cursor: 'pointer' }}
                >
                    Clear
                </div>
            </div>
        </>
    )

    useEffect(() => {
        if (isOpen) {
            const modalContainer = document.createElement('div')
            modalContainer.id = 'nosto-mobile-facets-modal'
            modalContainer.style.cssText = `
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                z-index: 99999;
                display: flex;
                flex-direction: column;
                background: white;
                overflow: hidden;
            `
            document.body.appendChild(modalContainer)
            document.body.style.overflow = 'hidden'

            render(modalContent, modalContainer)

            return () => {
                render(null, modalContainer)
                document.body.removeChild(modalContainer)
                document.body.style.overflow = ''
            }
        }
    }, [isOpen, modalContent]) // Include modalContent in dependencies

    return null
} 